import { useState, useEffect, useCallback } from 'react';
import supabaseService from '../services/supabaseService';
import { useAuth } from '../contexts/AuthContext';

export const useLinks = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasMore: false
  });

  const clearError = () => setError('');

  const loadLinks = useCallback(async (options = {}) => {
    if (!user) {
      console.log('❌ useLinks: No user found, skipping load');
      return;
    }

    try {
      console.log('🔄 useLinks: Loading links for user:', user);
      setLoading(true);
      clearError();

      const result = await supabaseService.getLinks(null, options);
      console.log('📊 useLinks: Got result:', result);
      
      if (options.page === 1 || !options.page) {
        console.log('📝 useLinks: Setting links:', result.links.length, 'links');
        setLinks(result.links);
      } else {
        // Append for pagination
        setLinks(prev => [...prev, ...result.links]);
      }

      console.log('📄 useLinks: Setting pagination:', {
        page: result.page,
        totalPages: result.totalPages,
        total: result.total,
        hasMore: result.hasMore
      });

      setPagination({
        page: result.page,
        totalPages: result.totalPages,
        total: result.total,
        hasMore: result.hasMore
      });
      
    } catch (err) {
      console.error('Failed to load links:', err);
      setError(err.message || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createLink = async (linkData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      clearError();
      
      console.log('🔗 Creating link with data:', linkData);
      const newLink = await supabaseService.createLink(linkData, null);
      console.log('✅ Link created:', newLink);
      
      // Add new link to the beginning of the list
      setLinks(prev => [newLink, ...prev]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      
      return newLink;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create link';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateLink = async (id, updates) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      clearError();
      
      const updatedLink = await supabaseService.updateLink(id, updates, null);
      
      setLinks(prev => prev.map(link => 
        link.id === id ? updatedLink : link
      ));
      
      return updatedLink;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update link';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteLink = async (id) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      clearError();
      
      await supabaseService.deleteLink(id, null);
      
      setLinks(prev => prev.filter(link => link.id !== id));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      
      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete link';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteLinks = async (ids) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      clearError();
      
      const results = await supabaseService.bulkDelete(ids, null);
      
      // Remove successfully deleted links
      const successfulDeletes = results.filter(r => r.success).map(r => r.id);
      if (successfulDeletes.length > 0) {
        setLinks(prev => prev.filter(link => !successfulDeletes.includes(link.id)));
        setPagination(prev => ({ 
          ...prev, 
          total: Math.max(0, prev.total - successfulDeletes.length) 
        }));
      }
      
      // Report any failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        const errorMessages = failures.map(f => `Link ${f.id}: ${f.error}`);
        setError(`Some links could not be deleted: ${errorMessages.join(', ')}`);
      }
      
      return results;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete links';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateLinks = async (ids, updates) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      clearError();
      
      const results = await supabaseService.bulkUpdate(ids, updates, null);
      
      // Update successfully updated links
      const successfulUpdates = results.filter(r => r.success);
      if (successfulUpdates.length > 0) {
        setLinks(prev => prev.map(link => {
          const update = successfulUpdates.find(u => u.id === link.id);
          return update ? update.link : link;
        }));
      }
      
      // Report any failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        const errorMessages = failures.map(f => `Link ${f.id}: ${f.error}`);
        setError(`Some links could not be updated: ${errorMessages.join(', ')}`);
      }
      
      return results;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update links';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const recordClick = async (shortCode, domainId, clickData) => {
    try {
      return await supabaseService.recordClick(shortCode, domainId, clickData);
    } catch (err) {
      console.error('Failed to record click:', err);
      throw err;
    }
  };

  const getLinkAnalytics = async (id) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      return await supabaseService.getLinkAnalytics(id, null);
    } catch (err) {
      console.error('Failed to get link analytics:', err);
      throw err;
    }
  };

  const getUserTags = useCallback(() => {
    if (!user) return [];
    return supabaseService.getUserTags(null); // Pass null instead of null to avoid UUID errors
  }, [user]);

  const getUserStats = useCallback(() => {
    if (!user) return null;
    return supabaseService.getUserStats(null);
  }, [user]);

  const refreshLinks = useCallback((options = {}) => {
    return loadLinks({ ...options, page: 1 });
  }, [loadLinks]);

  const loadMoreLinks = useCallback((options = {}) => {
    if (pagination.hasMore && !loading) {
      return loadLinks({ ...options, page: pagination.page + 1 });
    }
  }, [loadLinks, pagination.hasMore, pagination.page, loading]);

  // Load initial links
  useEffect(() => {
    if (user) {
      loadLinks();
    } else {
      setLinks([]);
      setPagination({ page: 1, totalPages: 1, total: 0, hasMore: false });
    }
  }, [user, loadLinks]);

  return {
    links,
    loading,
    error,
    pagination,
    createLink,
    updateLink,
    deleteLink,
    bulkDeleteLinks,
    bulkUpdateLinks,
    recordClick,
    getLinkAnalytics,
    getUserTags,
    getUserStats,
    loadLinks: refreshLinks,
    loadMoreLinks,
    clearError
  };
};